import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';
import { getThemeColors } from '../lib/theme';
import { selectableAdmins, type V1Admin } from '../lib/v1';

/**
 * Mitarbeiter-Mehrfachauswahl als Filter-Dropdown (Suche + Checkboxen).
 * Ursprünglich in der Anfragen-Liste, jetzt auch in der PB-Liste (/pflege).
 * Leere Auswahl = „Alle Zugewiesen". Die Optionen kommen von außen, damit der
 * Aufrufer bestimmt, welche Mitarbeiter wählbar sind.
 */
export interface AdminMultiFilterProps {
  dark: boolean;
  admins: V1Admin[];
  value: number[];
  onChange: (ids: number[]) => void;
  currentAdminId?: number | null;
}

export function AdminMultiFilter({ dark, admins, value, onChange, currentAdminId = null }: AdminMultiFilterProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Autofocus search field when dropdown opens.
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchRef.current?.focus(), 30);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Typeahead über Name + E-Mail. Wählbar sind nur aktive Mitarbeiter;
  // bereits gesetzte Filterwerte bleiben sichtbar (Namensauflösung über `admins`).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const options = selectableAdmins(admins, value);
    if (!q) return options;
    return options.filter(a => {
      const name = [a.vorname, a.nachname].filter(Boolean).join(' ').toLowerCase();
      return name.includes(q) || (a.email ?? '').toLowerCase().includes(q);
    });
  }, [admins, search, value]);

  const buttonLabel = value.length === 0
    ? (tr('anfragen.filter.assigned_all') || 'Alle Zugewiesen')
    : value.length === 1
      ? (() => {
          const found = admins.find(a => a.id === value[0]);
          return found
            ? [found.vorname, found.nachname].filter(Boolean).join(' ')
            : (tr('anfragen.filter.assigned_me') || 'Ich');
        })()
      : (tr('anfragen.filter.assigned_count', { count: value.length }) || `${value.length} Admins`);

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        aria-label={tr('anfragen.filter.assignee_aria')}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8,
          padding: '7px 10px', background: t.inputBg, color: t.text,
          fontFamily: 'Outfit,sans-serif', cursor: 'pointer',
        }}
      >
        {buttonLabel}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 8,
          padding: '6px 0', minWidth: 220, maxHeight: 360, overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: 4,
        }}>
          <div style={{ padding: '4px 12px 6px', position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 20, display: 'flex' }}><Icon.Search c={t.placeholder} size={13}/></span>
            <input
              ref={searchRef}
              type="text"
              aria-label={tr('anfragen.filter.assigned_search')}
              placeholder={tr('anfragen.filter.assigned_search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 7,
                padding: '6px 10px 6px 28px', width: '100%', boxSizing: 'border-box',
                background: t.inputBg, color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
              }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: t.text }}>
            <input type="checkbox"
              checked={value.length === 0}
              onChange={() => onChange([])}
            />
            {tr('anfragen.filter.assigned_all') || 'Alle Zugewiesen'}
          </label>
          {admins.length > 0 && filtered.length === 0 && (
            <div style={{ padding: '6px 12px', fontSize: 12, color: t.muted }}>
              {tr('common.search.empty')}
            </div>
          )}
          {filtered.map(a => {
            const label = [a.vorname, a.nachname].filter(Boolean).join(' ').trim() || a.email;
            const checked = value.includes(a.id);
            return (
              <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: t.text }}>
                <input type="checkbox"
                  checked={checked}
                  onChange={() => onChange(checked ? value.filter(id => id !== a.id) : [...value, a.id])}
                />
                {label}
                {a.id === currentAdminId && (
                  <span style={{ fontSize: 10, color: t.muted, marginLeft: 4 }}>
                    ({tr('anfragen.filter.assigned_me_suffix') || 'ich'})
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
